import PubSub from 'pubsub-js';
import {
  BufferAttribute,
  BufferGeometry,
  DoubleSide,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  Uint16BufferAttribute,
  Vector2,
  Vector3,
} from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import _ from 'underscore';

import {
  distanceToLineSegment,
  findClosestTriangleEdge,
  pointInTriangle,
} from '../../../utils/math/triangle';
import cameraManager from '../../camera-manager';
import { Sphere, SphereSprite } from '../../elements/supportGeom/sphere';
import { VolumeCalculationAsset } from '../../elements/volume/types';
import { MouseInputData } from '../../utilities/input-event/input-event-data-extractor';
import { Pick } from '../../utilities/pick';
import { isAssetPickable } from '../select-tool';
import { lineMaterial, updateLineMaterials } from './volume-boundary-polygon-edge';

/**
 *  This class defines the area that encloses volume calculation of an area.
 *  Mainly if contains the area and logic to modify it using indexed vertices
 *  in a dynamic BufferGeometry.
 *
 *  Additional work:
 *  Identify and block overlapping edges and triangles.
 *  Picking dynamic BufferGeometries.
 */
interface VertexPoint {
  vertexIndex: number;
  sphere: Sphere;
}

interface GhostPoint {
  edge?: Edge;
  sphere: Sphere;
}

interface Edge {
  v0: number;
  v1: number;
}

interface Edges {
  [key: number]: {
    [key: number]: { count: number; lines: [Line2, Line2] };
  };
}

interface IntersectionState {
  intersectsArea: boolean;
  pickingGhostPoint: boolean;

  pickedPoint?: Vector3;

  edge: {
    picked: boolean;
    closest?: Edge;
  };
  vertex: {
    picked: boolean;
    closest?: VertexPoint;
  };
}

interface PickedVertexPoint {
  point: VertexPoint;
  oldPosition: Vector3;
}

/**
 *  TODO - Refactor how to handle these edges.
 *  Extract to its own class that handles picking, visibility etc.
 */
interface EdgeLine {
  backgroundLine: Line2;
  accentLine: Line2;
}

/**
 *  Sphere colors
 */
const sphereColors = {
  accent: 0xfed530,
  selected: 0x55ff55,
  ghost: 0x888,
};

/**
 *  The render orders of the different elements of the boundary polygon.
 */
const renderOrders = {
  lineBackground: 9000,
  lineAccent: 9005,
  vertexPoint: 9010,
  polygon: 900,
};

export class VolumeBoundaryPolygon extends Object3D {
  /**
   *  Returns true if there is at least on placed triangle in the polygon.
   *  Depends if the polygon is currently being edited or not.
   */
  get hasValidPolygon() {
    return this.isEditing ? this.indexCount > 3 : this.triangleCount > 0;
  }

  get triangleCount() {
    return Math.floor(this.indexCount / 3);
  }

  get vertexCount() {
    return this.vertexPoints.length;
  }

  get limitationMesh(): Mesh | undefined {
    if (!this.areaMesh) {
      this.buildPickingMesh();
    }
    return this.areaMesh;
  }

  get isEdgeLocked() {
    return !!this.pickedEdge;
  }

  get isVertexPicked() {
    return !!this.pickedVertex;
  }

  get isEditing() {
    return this._isEditing;
  }

  private _isEditing = false;

  // This is an arbitrary limit to be able to pre-create the buffer geometry.
  private readonly maxPolygonPoints = 300;
  private readonly maxTriangles = 300;

  private indexCount = 0;

  private targetAsset?: VolumeCalculationAsset;

  private edges: Edges = {};
  private hoveredEdge?: Edge;
  private pickedEdge?: Edge;

  private geometry: BufferGeometry;

  private vertexPoints: VertexPoint[] = [];

  private areaMesh?: Mesh;

  private cursor: Sphere;

  private helperLine?: EdgeLine;

  private hoveredVertex?: VertexPoint;

  private ghostPoint: GhostPoint;
  private pickedVertex?: PickedVertexPoint;
  constructor() {
    super();

    this.geometry = this.initializeGeometry();

    this.cursor = new Sphere(new Vector3(), SphereSprite.DotPlain, { color: sphereColors.accent });
    this.cursor.userData = {
      'boundary-polygon': true,
    };
    this.add(this.cursor);

    this.cursor.renderOrder = renderOrders.vertexPoint;
    const ghostSphere = new Sphere(new Vector3(), SphereSprite.DotPlain, {
      color: sphereColors.ghost,
    });
    ghostSphere.renderOrder = renderOrders.vertexPoint;
    ghostSphere.userData = {
      'boundary-polygon': true,
    };

    this.add(ghostSphere);

    this.ghostPoint = { sphere: ghostSphere, edge: undefined };
  }

  /**
   *  Adds a new point to the area polygon.
   *
   * @param point - The new point to be added to the polygon.
   */
  onMouseUp(e: MouseInputData) {
    if (this.pickedVertex) {
      this.pickedVertex = undefined;
      return;
    }

    const intersections = this.resolvePicking(e);
    if (!intersections) {
      return;
    }

    if (intersections.pickingGhostPoint) {
      this.createVertexPointAtGhostPoint();
      this.hoveredEdge = undefined;
      return true;
    }

    if (intersections.edge.picked && !intersections.vertex.picked) {
      this.setPickedEdge(intersections.edge.closest!);
      return true;
    }

    // If we pick a vertex without an edge selected, select the vertex.
    if (!this.pickedEdge && intersections.vertex.picked && !this.pickedVertex) {
      this.pickedVertex = {
        point: intersections.vertex.closest!,
        oldPosition: intersections.vertex.closest!.sphere.position.clone(),
      };

      this.remove(this.ghostPoint.sphere);
      return;
    }

    if (!this.cursor || (!intersections.vertex.picked && intersections.intersectsArea)) {
      return;
    }

    switch (this.vertexPoints.length) {
      case 0:
        this.addNewVertexToTriangleIndex(this.cursor.position.clone());
        break;
      case 1:
        /**
         *  If we are currently drawing a line (second vertex in the polygon).
         *  Add the new vertex point if the first vertex point wasn't picked.
         */
        if (intersections.vertex.picked) {
          return;
        }
        this.addNewVertexToTriangleIndex(this.cursor.position.clone());
        this.addNewVertexToTriangleIndex(this.cursor.position.clone());
        break;

      /**
       *  If we are visualizing a triangle, lock it and create a new visual triangle.
       */
      default:
        this.addTriangleEdges(this.triangleCount - 1);

        /**
         *  Create a new point where the cursor is, and define a new triangle using the same vertex.
         *  Then let the update function correct the placement.
         *
         *  If picked an existing vertex as the last triangle vertex, the latest vertex is not used
         *  and can be reused here.
         */
        if (intersections.vertex.picked) {
          this.addExistingVertexToTriangleIndex(this.vertexCount - 1);
        } else {
          this.addNewVertexToTriangleIndex(this.cursor.position.clone());
        }
        this.addExistingVertexToTriangleIndex(this.vertexCount - 1);
        this.addExistingVertexToTriangleIndex(this.vertexCount - 1);

        // If an edge is picked, release it.
        this.pickedEdge = undefined;
        break;
    }

    this.geometry.setDrawRange(0, this.indexCount - (this.indexCount % 3));
  }

  /**
   *  Updates the latest vertex point with a new position.
   *
   * @param position - The position to set the latest vertex point to.
   */
  onMouseMove(e: MouseInputData) {
    this.resetHoveredEdge();

    if (!this.targetAsset) {
      return;
    }

    const intersections = this.resolvePicking(e);
    if (!intersections) {
      return;
    }

    let pickedPosition = intersections.pickedPoint!;

    if (this.pickedVertex) {
      this.updateVertexPointPositionAtIndex(this.pickedVertex.point.vertexIndex, pickedPosition);
      this.resolveDisplayingNextTriangle(intersections);
      return;
    }

    if (intersections.edge.picked && !intersections.vertex.picked) {
      this.setHoveringEdge(intersections.edge.closest!);
    }

    this.updateGhostPoint(intersections.edge.closest);

    if (intersections.pickingGhostPoint) {
      this.ghostPoint.sphere.setScaleModifier(1.5);
    } else {
      this.ghostPoint.sphere.setScaleModifier(1);
    }

    /**
     *  If we picked a vertex, copy its position and tag it for animation.
     */
    if (intersections.vertex.picked) {
      if (this.pickedEdge && intersections.vertex.picked) {
        pickedPosition = intersections.vertex.closest!.sphere.getWorldPosition(new Vector3());
      }
      this.setHoveredVertexPoint(intersections.vertex.closest!);
    } else if (this.hoveredVertex) {
      this.resetHoveredVertexPoint();
    }

    this.resolveDisplayingNextTriangle(intersections);

    this.handleIntersectingPolygon(intersections);

    /**
     *  If the position intersects the polygon, return.
     */
    if (
      !pickedPosition ||
      (!intersections.vertex.picked && (intersections.edge.picked || intersections.intersectsArea))
    ) {
      return;
    }

    this.cursor.position.copy(pickedPosition);
    switch (this.vertexCount) {
      // If no vertices exist, do nothing.
      case 0:
        break;

      // If a single vertex exist, display a line between the first and the new position.
      case 1:
        this.setHelperLine(this.vertexPoints[0].sphere.position, pickedPosition);
        break;

      // If there are a triangle being drawn, identify the nearest edge, and update the visual (latests) triangle.
      default:
        /**
         *  If we did pick an existing vertex point, update the current triangle z-index.
         *  Else reset to the latest vertex added, and update the position.
         */
        if (intersections.vertex.picked) {
          this.geometry.index!.setZ(
            this.triangleCount - 1,
            intersections.vertex.closest!.vertexIndex,
          );
          this.geometry.index!.needsUpdate = true;
        } else {
          this.geometry.index!.setZ(this.triangleCount - 1, this.vertexCount - 1);
          this.updatePositionBuffer(
            this.geometry.index!.getZ(this.triangleCount - 1),
            pickedPosition,
          );
        }

        if (this.pickedEdge) {
          this.geometry.index!.setX(this.triangleCount - 1, this.pickedEdge.v0);
          this.geometry.index!.setY(this.triangleCount - 1, this.pickedEdge.v1);
        } else if (intersections.edge.closest) {
          this.geometry.index!.setX(this.triangleCount - 1, intersections.edge.closest.v0);
          this.geometry.index!.setY(this.triangleCount - 1, intersections.edge.closest.v1);
        }

        break;
    }

    if (this.helperLine) {
      this.helperLine.accentLine.visible = this.vertexPoints.length === 1;
      this.helperLine.backgroundLine.visible = this.vertexPoints.length === 1;
    }
  }

  /**
   *  Returns true if intersecting one of the area triangles in <x,y> plane.
   *
   *  @param point - The point to check for area intersection.
   */
  intersectsArea(point: Vector3): boolean {
    const point2d = new Vector2(point.x, point.y);
    const nrOfTriangles = this.triangleCount - (this.indexCount % 3 ? 0 : 1);
    // Ensure we do not compare current triangle being created.
    for (let i = 0; i < nrOfTriangles; i++) {
      const vertexPositions = [
        this.vertexPoints[this.geometry.index!.getX(i)].sphere.getWorldPosition(new Vector3()),
        this.vertexPoints[this.geometry.index!.getY(i)].sphere.getWorldPosition(new Vector3()),
        this.vertexPoints[this.geometry.index!.getZ(i)].sphere.getWorldPosition(new Vector3()),
      ];
      if (
        pointInTriangle(
          point2d,
          new Vector2(vertexPositions[0].x, vertexPositions[0].y),
          new Vector2(vertexPositions[1].x, vertexPositions[1].y),
          new Vector2(vertexPositions[2].x, vertexPositions[2].y),
        )
      ) {
        return true;
      }
    }

    return false;
  }

  /**
   *  Hides the editing triangle from rendering.
   */
  displayEditingGeometry(displayState: boolean) {
    if (this.vertexCount < 2) {
      return;
    }

    this.cursor.visible = displayState;
    this.geometry.setDrawRange(0, this.indexCount - (this.indexCount % 3) - (displayState ? 0 : 3));

    if (displayState) {
      this.add(this.vertexPoints[this.vertexPoints.length - 1].sphere);
    } else {
      this.remove(this.vertexPoints[this.vertexPoints.length - 1].sphere);
    }
  }

  animate() {
    for (const point of this.vertexPoints) {
      point.sphere.onAnimate();
    }

    this.cursor.onAnimate();
    this.ghostPoint.sphere.onAnimate();

    updateLineMaterials();
  }

  /**
   *  Starts a new or picks up the previous area creation.
   */
  startTriangleCreation() {
    this.add(this.cursor);

    /**
     *  Reset area mesh when (re-)start editing the polygon.
     */
    if (this.areaMesh) {
      this.areaMesh.geometry.dispose();
      this.areaMesh = undefined;
    }

    /**
     *  When re-starting and an edge is found, setup the visual triangle.
     */
    if (this.triangleCount > 0) {
      this.addNewVertexToTriangleIndex(this.cursor.position);
      this.addExistingVertexToTriangleIndex(this.vertexCount - 1);
      this.addExistingVertexToTriangleIndex(this.vertexCount - 1);
    }

    this.geometry.setDrawRange(0, this.indexCount - (this.indexCount % 3));

    this._isEditing = true;
  }

  /**
   *  Stops area creation and remove the current unfinished triangle being created, if any.
   */
  endTriangleCreation() {
    if (this.isEditing) {
      this.removeUnfinishedTriangle();
      this.remove(this.cursor);
      this.remove(this.helperLine?.accentLine!);
      this.remove(this.helperLine?.backgroundLine!);
    }

    this.resetHoveredEdge();
    this.resetPickedEdge();
    this.remove(this.ghostPoint.sphere);

    // This is a hack to handle picking because issues of picking dynamic BufferGeometry.
    // TODO: Handle correctly picking of a dynamic GeometryBuffer.
    this.buildPickingMesh();

    this._isEditing = false;
  }

  /**
   *  Returns true if the mouse event is intersects with the polygon.
   *
   * @param inputData - The input data to check intersection against.
   */
  isIntersecting(inputData: MouseInputData) {
    if (!this.targetAsset) {
      return false;
    }

    const intersections = this.resolvePicking(inputData);
    if (
      !(
        intersections &&
        (intersections.intersectsArea || intersections.edge.picked || intersections.vertex.picked)
      )
    ) {
      return false;
    }

    this.startTriangleCreation();
    this.onMouseUp(inputData);

    return true;
  }

  /**
   *  Resets the volume boundary polygon to a new dxf asset.
   *
   * @param asset - The asset that is the target for the volume boundary polygon .
   */
  reset(asset: VolumeCalculationAsset) {
    if (this.targetAsset) {
      this.targetAsset.remove(this);
    }

    this.clearCustom();

    this.targetAsset = asset;
    this.targetAsset.add(this);
  }

  // Three has its own Clear function, maybe that can be used
  clearCustom() {
    this.indexCount = 0;
    this.vertexPoints.map((point) => {
      this.remove(point.sphere);
      point.sphere.dispose();
    });
    this.vertexPoints = [];
    this.remove(this.helperLine?.accentLine!);
    this.remove(this.helperLine?.backgroundLine!);

    this.geometry = this.initializeGeometry();

    this.clearEdges();

    if (this.areaMesh) {
      this.areaMesh.geometry.dispose();
      this.areaMesh = undefined;
    }
  }

  unlockEdge() {
    this.resetPickedEdge();
  }

  releasePickedVertex() {
    if (!this.pickedVertex) {
      return;
    }

    this.updateVertexPointPositionAtIndex(
      this.pickedVertex.point.vertexIndex,
      this.pickedVertex.oldPosition,
    );
    this.pickedVertex = undefined;

    this.resetHoveredVertexPoint();
  }

  /**
   * Returns a value indicating whether the context menu should be displayed at the given position.
   */
  showContextMenu(inputData: MouseInputData) {
    const vertexPoint = this.findClosestVertexPoint(inputData.pointer.client);
    return vertexPoint[1] && !this.pickedVertex;
  }

  /**
   * Returns a list of menu items to display in context menu.
   */
  getContextMenuItems(inputData: MouseInputData) {
    const vertexPoint = this.findClosestVertexPoint(inputData.pointer.client);
    const vertexIndex = vertexPoint[0]!.vertexIndex;
    return [
      {
        title: 'Ta bort punkt',
        click: () => {
          this.deleteVertexPointAtIndex(vertexIndex);
        },
      },
    ];
  }

  /**
   *  Resets and initializes the polygon geometry.
   */
  private initializeGeometry() {
    this.remove(...this.children);

    if (this.geometry) {
      this.geometry.dispose();
    }

    const positions = new Float32Array(this.maxPolygonPoints * 3);
    const indices = new Uint16Array(_.times(this.maxTriangles * 3, () => -1));

    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(positions, 3));
    geometry.setIndex(new BufferAttribute(indices, 3));

    geometry.setDrawRange(0, this.indexCount - (this.indexCount % 3));

    const mesh = new Mesh(
      geometry,
      new MeshBasicMaterial({
        color: sphereColors.accent,
        transparent: true,
        opacity: 0.7,
        side: DoubleSide,
        depthTest: false,
      }),
    );

    mesh.userData = {
      'boundary-polygon': true,
    };

    this.add(mesh);

    mesh.renderOrder = renderOrders.polygon;

    return geometry;
  }

  /**
   *  Returns the intersected information of the polygon.
   *
   * @param e - The mouse input data.
   */
  private resolvePicking(e: MouseInputData): IntersectionState | undefined {
    const [closestVertex, pickedVertex] = this.findClosestVertexPoint(e.pointer.client);
    const [edge, pickedEdge] = this.getClosestPolygonEdge(e.pointer.client) ?? [undefined, false];
    let pickedPoint = Pick.instance.pointcloudPoint(e.pointer.position.x, e.pointer.position.y, {
      rayPointThreshold: 0.5,
    });
    const pickingGhostPoint = this.isPickingGhostPoint(e.pointer.client);

    if (!pickedPoint) {
      const filter = isAssetPickable;
      const intersections = Pick.instance.assets(
        e.pointer.position.x,
        e.pointer.position.y,
        filter,
      );

      if (intersections.length === 0) {
        return;
      }

      pickedPoint = intersections[0].point;
    }

    return {
      // Convert the picked position to the local position of the asset.
      // We do this because the boundary polygon is attached directly to the asset.
      pickedPoint: this.targetAsset!.worldToLocal(pickedPoint.clone()),
      edge: {
        picked: pickedEdge,
        closest: edge,
      },
      vertex: {
        picked: pickedVertex,
        closest: closestVertex,
      },
      intersectsArea: !!pickedPoint && this.intersectsArea(pickedPoint),
      pickingGhostPoint,
    };
  }

  /**
   *  Create a static geometry based on the dynamic geometry for picking purpose.
   *
   *  TODO: Handle picking on dynamic BufferGeometry.
   */
  private buildPickingMesh() {
    const geometry = new BufferGeometry();
    const triangles = this.triangleCount;

    if (triangles === 0) {
      return;
    }

    geometry.setFromPoints(
      this.vertexPoints.map((vp) => this.localToWorld(vp.sphere.position.clone())),
    );
    const indices = this.geometry.index!.array.slice(0, triangles * 3);
    geometry.setIndex(new Uint16BufferAttribute(indices, 1));

    geometry.computeBoundingSphere();
    geometry.computeBoundingBox();

    this.areaMesh = new Mesh(geometry, new MeshBasicMaterial({ side: DoubleSide }));
  }

  /**
   *  Updates the position of the latest added vertex point.
   *
   * @param position . The position to update the position buffer with.
   */
  private updatePositionBuffer(vertexIndex: number, position: Vector3) {
    const positions = this.geometry.attributes.position;
    positions.setXYZ(vertexIndex, position.x, position.y, position.z);

    (this.geometry.attributes.position as BufferAttribute).needsUpdate = true;
    this.geometry.index!.needsUpdate = true;
    this.geometry.computeBoundingSphere();

    // Update the vertex point position as well.
    this.vertexPoints[vertexIndex].sphere.position.copy(position);
  }

  /**
   *  Returns the closest valid vertex point of a position.
   *  Also returns if the vertex point is directly picked or not.
   *
   * @param point - The screen point from which the closest vertex point is returned.
   */
  private findClosestVertexPoint(point: Vector2): [VertexPoint | undefined, boolean] {
    const camera = cameraManager().getActiveCamera();
    if (!camera || this.vertexPoints.length === 0) {
      return [undefined, false];
    }

    /**
     * Checks if a vertex point is part of the latest triangle.
     *
     *  TODO: Add check that the vertex point does not create a line overlapping existing edges.
     *  Not included in version one.
     *
     * @param vertexPoint - The vertex point to compare towards the vertex list
     */
    const isValidVertex = (vertexPoint: VertexPoint) => {
      if (vertexPoint === this.vertexPoints[this.vertexPoints.length - 1]) {
        return false;
      }
      switch (this.vertexCount) {
        case 0:
          return true;
        case 1:
          return this.vertexPoints[0] !== vertexPoint;
        case 2:
          return this.vertexPoints[0] !== vertexPoint && this.vertexPoints[1] !== vertexPoint;
        default:
          return (
            this.vertexPoints[this.getVertexIndex(this.triangleCount, 0)] !== vertexPoint &&
            this.vertexPoints[this.getVertexIndex(this.triangleCount, 1)] !== vertexPoint
          );
      }
    };

    let closestDistance = Number.MAX_SAFE_INTEGER;
    let closestPoint: VertexPoint | undefined;
    for (const vertexPoint of this.vertexPoints) {
      /**
       *  Verify that we are not finding the current triangle being created or active vertex point .
       */
      if (this.isEditing && !isValidVertex(vertexPoint)) {
        continue;
      }

      const vertexPos = vertexPoint.sphere.getWorldPosition(new Vector3());
      const distance = camera.getScreenPosition(vertexPos).sub(point).lengthSq();
      if (distance < closestDistance) {
        closestDistance = distance;
        closestPoint = vertexPoint;
      }
    }

    const pickingThreshold = 144;
    return [closestPoint, closestDistance < pickingThreshold];
  }

  /**
   *  Handles displaying help geometry based on the position is intersecting the existing polygon.
   *
   *  @param intersections - The intersection state.
   */
  private handleIntersectingPolygon(intersections: IntersectionState) {
    if (this.vertexPoints.length === 0) {
      return false;
    }

    const hideHelpGeometry =
      intersections.intersectsArea || intersections.edge.picked || intersections.vertex.picked;
    if (hideHelpGeometry) {
      this.remove(this.helperLine?.backgroundLine!);
      this.remove(this.helperLine?.accentLine!);
      this.remove(this.cursor);
      this.remove(this.vertexPoints[this.vertexPoints.length - 1].sphere);
    } else {
      this.add(this.cursor);
      this.add(this.vertexPoints[this.vertexPoints.length - 1].sphere);
    }
  }

  /**
   *  Resolves if the edited triangle is to be displayed or not.
   *
   *  @param position - The intersection state.
   */
  private resolveDisplayingNextTriangle(intersections: IntersectionState) {
    if (this.indexCount < 2) {
      return;
    }
    let displayNextTriangle;
    if (intersections.vertex.picked) {
      displayNextTriangle = !intersections.intersectsArea || !!this.pickedEdge;
    } else {
      displayNextTriangle = !intersections.intersectsArea && !intersections.edge.picked;
    }

    this.displayEditingGeometry(displayNextTriangle && !this.pickedVertex);
  }

  /**
   *  Registers all of the edges of a triangle.
   *
   *  @param triangleIndex - Thr triangle which edges should be added-
   */
  private addTriangleEdges(triangleIndex: number) {
    const v0Index = this.getVertexIndex(triangleIndex, 0);
    const v1Index = this.getVertexIndex(triangleIndex, 1);
    const v2Index = this.getVertexIndex(triangleIndex, 2);
    this.addEdge(v0Index, v1Index);
    this.addEdge(v1Index, v2Index);
    this.addEdge(v2Index, v0Index);
  }

  /**
   *  Remove all of a triangles edges.
   *
   *  @param triangleIndex - Thr triangle which edges should be removes-
   */
  private removeTriangleEdges(triangleIndex: number) {
    const v0Index = this.getVertexIndex(triangleIndex, 0);
    const v1Index = this.getVertexIndex(triangleIndex, 1);
    const v2Index = this.getVertexIndex(triangleIndex, 2);
    this.removeEdge(v0Index, v1Index);
    this.removeEdge(v1Index, v2Index);
    this.removeEdge(v2Index, v0Index);
  }

  /**
   *  Tags a vertex point as hovered.
   *
   * @param vertexPoint - The vertex point to set as hovered.
   */
  private setHoveredVertexPoint(vertexPoint: VertexPoint) {
    if (this.hoveredVertex) {
      this.resetHoveredVertexPoint();
    }

    this.hoveredVertex = vertexPoint;
    this.hoveredVertex.sphere.setScaleModifier(1.5);
    this.hoveredVertex.sphere.setColor(sphereColors.selected);
  }

  /**
   *  Removes the hovered tag from the vertex point if there is one.
   *
   * @param vertexPoint - The vertex point to set as hovered.
   */
  private resetHoveredVertexPoint() {
    if (!this.hoveredVertex) {
      return;
    }

    this.hoveredVertex.sphere.setColor(sphereColors.accent);
    this.hoveredVertex.sphere.setScaleModifier(1);
    this.hoveredVertex = undefined;
  }

  /**
   *  Highlights and referenced the edge as the hovered edge.
   *
   *  @param edge - The edge to be highlighted and referenced as hovered.
   */
  private setHoveringEdge(edge: Edge) {
    if (!this.isPickedEdge(edge)) {
      const lowerIndex = edge.v0 < edge.v1 ? edge.v0 : edge.v1;
      const higherIndex = edge.v0 > edge.v1 ? edge.v0 : edge.v1;
      this.edges[lowerIndex][higherIndex].lines[0].material = lineMaterial.focusedBackground;
      this.edges[lowerIndex][higherIndex].lines[1].material = lineMaterial.hovered;
      this.hoveredEdge = { v0: lowerIndex, v1: higherIndex };
    }
  }

  /**
   *  Resets the hovered edge reference and restores the assigned material.
   */
  private resetHoveredEdge() {
    if (this.hoveredEdge && !this.isPickedEdge(this.hoveredEdge)) {
      const lowerIndex =
        this.hoveredEdge.v0 < this.hoveredEdge.v1 ? this.hoveredEdge.v0 : this.hoveredEdge.v1;
      const higherIndex =
        this.hoveredEdge.v0 > this.hoveredEdge.v1 ? this.hoveredEdge.v0 : this.hoveredEdge.v1;
      this.edges[lowerIndex][higherIndex].lines[0].material = lineMaterial.background;
      this.edges[lowerIndex][higherIndex].lines[1].material = lineMaterial.accent;

      this.hoveredEdge = undefined;
    }
  }

  /**
   *  Resets the picked edge reference and restores the assigned material.
   */
  private resetPickedEdge() {
    if (this.pickedEdge) {
      const lowerIndex =
        this.pickedEdge.v0 < this.pickedEdge.v1 ? this.pickedEdge.v0 : this.pickedEdge.v1;
      const higherIndex =
        this.pickedEdge.v0 > this.pickedEdge.v1 ? this.pickedEdge.v0 : this.pickedEdge.v1;
      this.edges[lowerIndex][higherIndex].lines[0].material = lineMaterial.background;
      this.edges[lowerIndex][higherIndex].lines[1].material = lineMaterial.accent;

      this.pickedEdge = undefined;
    }
  }

  /**
   *  Highlights and referenced the edge as the picked edge.
   *
   *  @param edge - The edge to be highlighted and referenced as hovered.
   */
  private setPickedEdge(edge: Edge) {
    const lowerIndex = edge.v0 < edge.v1 ? edge.v0 : edge.v1;
    const higherIndex = edge.v0 > edge.v1 ? edge.v0 : edge.v1;
    if (this.isPickedEdge(edge)) {
      this.edges[lowerIndex][higherIndex].lines[0].material = lineMaterial.background;
      this.edges[lowerIndex][higherIndex].lines[1].material = lineMaterial.accent;
      this.pickedEdge = undefined;
      return;
    } else if (this.pickedEdge) {
      this.edges[this.pickedEdge.v0][this.pickedEdge.v1].lines[0].material =
        lineMaterial.background;
      this.edges[this.pickedEdge.v0][this.pickedEdge.v1].lines[1].material = lineMaterial.accent;
    }
    this.edges[lowerIndex][higherIndex].lines[0].material = lineMaterial.focusedBackground;
    this.edges[lowerIndex][higherIndex].lines[1].material = lineMaterial.selected;
    this.pickedEdge = { v0: lowerIndex, v1: higherIndex };
  }

  /**
   * Returns if the edge parameter is the picked edge.
   *
   * @param edge - The edge to check if it is the picked edge.
   */
  private isPickedEdge(edge: Edge) {
    if (this.pickedEdge) {
      const lowerIndex = edge.v0 < edge.v1 ? edge.v0 : edge.v1;
      const higherIndex = edge.v0 > edge.v1 ? edge.v0 : edge.v1;
      return this.pickedEdge?.v0 === lowerIndex && this.pickedEdge?.v1 === higherIndex;
    }
    return false;
  }

  /**
   *  Increases the count of the vertex index edge pair.
   *
   * @param v0 - The first vertex index of the edge.
   * @param v1 - The second vertex index of the edge.
   */
  private addEdge(v0: number, v1: number) {
    const lowerIndex = v0 < v1 ? v0 : v1;
    const largerIndex = v0 > v1 ? v0 : v1;
    this.edges[lowerIndex] = this.edges[lowerIndex] || {};
    this.edges[lowerIndex][largerIndex] = this.edges[lowerIndex][largerIndex] || {
      count: 0,
      lines: this.createFatLines(v0, v1),
    };
    this.edges[lowerIndex][largerIndex].count =
      (this.edges[lowerIndex][largerIndex]?.count ?? 0) + 1;
    if (this.edges[lowerIndex][largerIndex].count < 2) {
      this.add(...this.edges[lowerIndex][largerIndex].lines);
    } else {
      this.remove(...this.edges[lowerIndex][largerIndex].lines);
    }
  }

  /**
   *  Reduces (and removes) the count of the vertex index edge pair.
   *
   * @param v0 - The first vertex index of the edge.
   * @param v1 - The second vertex index of the edge.
   */
  private removeEdge(v0: number, v1: number) {
    const lowerIndex = v0 < v1 ? v0 : v1;
    const largerIndex = v0 > v1 ? v0 : v1;
    // If no edges are registered, there is nothing to remove.
    if (!this.edges[lowerIndex] || !this.edges[lowerIndex][largerIndex]) {
      return;
    }

    this.edges[lowerIndex][largerIndex].count = Math.max(
      0,
      this.edges[lowerIndex][largerIndex].count - 1,
    );
    if (this.edges[lowerIndex][largerIndex].count === 0) {
      this.remove(...this.edges[lowerIndex][largerIndex].lines);
      for (const line of this.edges[lowerIndex][largerIndex].lines) {
        line.geometry.dispose();
      }
      delete this.edges[lowerIndex][largerIndex];
    }
  }

  /**
   *  Clears all of the edges of the polygon.
   */
  private clearEdges() {
    this.resetHoveredEdge();
    this.resetPickedEdge();

    for (const edgeArray of Object.values(this.edges)) {
      for (const edge of Object.values(edgeArray as { [key: number]: { lines: [Line2, Line2] } })) {
        const lines = edge.lines;
        lines[0].geometry.dispose();
        lines[1].geometry.dispose();
        this.remove(...lines);
      }
    }

    this.edges = {};
  }

  /**
   *  Create a 'fat' line that support line width.
   *
   * @param v0 - The first line point.
   * @param v1 - The second line point.
   */
  private createFatLines(v0: number, v1: number) {
    const vPos0 = this.vertexPoints[v0].sphere.position;
    const vPos1 = this.vertexPoints[v1].sphere.position;
    const geometry = new LineGeometry();
    geometry.setPositions([vPos0.x, vPos0.y, vPos0.z, vPos1.x, vPos1.y, vPos1.z]);

    const backgroundLine = new Line2(geometry, lineMaterial.background);
    const yellowLine = new Line2(geometry, lineMaterial.accent);

    yellowLine.renderOrder = renderOrders.lineAccent;
    backgroundLine.renderOrder = renderOrders.lineBackground;
    return [backgroundLine, yellowLine];
  }

  /**
   *  Returns if the edge is an available edge.
   *  It is available if it has less than 2 edge counts in the pair.
   *
   * @param v0 - The first vertex index of the edge.
   * @param v1 - The second vertex index of the edge.
   */
  private isAvailableEdge(v0: number, v1: number) {
    const lowerIndex = v0 < v1 ? v0 : v1;
    const largerIndex = v0 > v1 ? v0 : v1;
    return (
      !this.edges[lowerIndex] ||
      !this.edges[lowerIndex][largerIndex] ||
      this.edges[lowerIndex][largerIndex].count < 2
    );
  }

  /**
   *  Create a new vertex point at the position.
   *
   * @param position - The position where the vertex point should be created.
   */
  private createVertexPoint(position: Vector3) {
    PubSub.publish('onVolumeBoundaryPolygonChanged', this);

    const sphere = new Sphere(position, SphereSprite.DotPlain, { color: sphereColors.accent });
    sphere.userData = {
      'boundary-polygon': true,
    };
    sphere.renderOrder = renderOrders.vertexPoint;
    const vertexPoint = { sphere, vertexIndex: this.vertexPoints.length };

    this.add(sphere);
    this.vertexPoints.push(vertexPoint);

    return vertexPoint;
  }

  /**
   *  Removes a vertex point at the vertex index value.
   *
   * @param vertexIndex - The index of the vertex to delete.
   */
  private deleteVertexPointAtIndex(vertexIndex: number) {
    // Remove the editing triangle.
    this.removeUnfinishedTriangle();

    for (let i = 0; i < this.triangleCount; i++) {
      if (
        this.geometry.index!.getX(i) === vertexIndex ||
        this.geometry.index!.getY(i) === vertexIndex ||
        this.geometry.index!.getZ(i) === vertexIndex
      ) {
        this.deleteTriangle(i);
      }
    }

    const [vertices, indices] = this.remapGeometryData();

    // We have the new data, remove the old one.
    this.clearCustom();

    // Create all of the vertices.
    for (const newVertex of vertices) {
      this.createVertexPoint(newVertex);
      this.updatePositionBuffer(this.vertexCount - 1, newVertex);
    }

    // Add all of the new indices.
    for (const newIndex of indices) {
      this.addExistingVertexToTriangleIndex(newIndex);
    }

    // Rebuild the edges.
    this.rebuildEdges();

    // If we are editing, recreated the edit triangle.
    if (this.isEditing && this.vertexCount >= 3) {
      this.addNewVertexToTriangleIndex(this.vertexPoints[this.vertexCount - 1].sphere.position);
      this.addExistingVertexToTriangleIndex(this.vertexCount - 1);
      this.addExistingVertexToTriangleIndex(this.vertexCount - 1);
    }

    this.geometry.setDrawRange(0, this.indexCount);
  }

  /**
   *  Deletes the triangle at the index.
   *
   *  @param index - The index of the triangle to be deleted-
   */
  private deleteTriangle(index: number) {
    const vertices = [
      this.geometry.index!.getX(index),
      this.geometry.index!.getY(index),
      this.geometry.index!.getZ(index),
    ];
    this.geometry.index!.setXYZ(index, -1, -1, -1);

    // Reduce the vertex triangle reference of the affected vertices.
    // If a vertex points is no longer referenced, reset the index.
    for (const vertexIndex of vertices) {
      if (!this.isVertexReferenced(vertexIndex)) {
        this.vertexPoints[vertexIndex].vertexIndex = -1;
      }
    }
  }

  /**
   *  Remaps the vertices and faces based on the remaining geometry data.
   */
  private remapGeometryData(): [Vector3[], number[]] {
    const newVertexArray: Vector3[] = [];
    const mappedIndices: { [key: number]: number } = {};
    for (const vertex of this.vertexPoints) {
      // Skip removed vertex points
      if (vertex.vertexIndex === -1) {
        continue;
      }

      mappedIndices[vertex.vertexIndex] = newVertexArray.length;
      newVertexArray.push(vertex.sphere.position);
    }

    // Iterate through the existing faces, and remap based in the new vertex array.
    const newIndexArray = [];
    for (let i = 0; i < this.triangleCount; i++) {
      const vertexIndex = [
        this.geometry.index!.getX(i),
        this.geometry.index!.getY(i),
        this.geometry.index!.getZ(i),
      ];
      // If a vertex is removed/unreferenced, skip this triangle.
      if (
        !this.vertexPoints[vertexIndex[0]] ||
        this.vertexPoints[vertexIndex[0]].vertexIndex === -1 ||
        !this.vertexPoints[vertexIndex[1]] ||
        this.vertexPoints[vertexIndex[1]].vertexIndex === -1 ||
        !this.vertexPoints[vertexIndex[2]] ||
        this.vertexPoints[vertexIndex[2]].vertexIndex === -1
      ) {
        continue;
      }

      newIndexArray.push(
        mappedIndices[vertexIndex[0]],
        mappedIndices[vertexIndex[1]],
        mappedIndices[vertexIndex[2]],
      );
    }

    return [newVertexArray, newIndexArray];
  }

  /**
   *  Removes all of the existing edges and rebuilds them.
   */
  private rebuildEdges() {
    this.clearEdges();

    _.times(this.triangleCount, (triangleIndex: number) => {
      this.addTriangleEdges(triangleIndex);
    });
  }

  /**
   *  Adds a new vertex point and adds it to the buffer and the current index of the current triangle.
   *
   * @param triangleId - The id of the triangle to add the vertex index to.
   * @param point - The point that will be a new vertex point that will be attached to the triangle index.
   */
  private addNewVertexToTriangleIndex(point: Vector3) {
    const vertexIndex = this.vertexPoints.length;
    this.updateTriangleIndex(this.triangleCount, this.indexCount % 3, vertexIndex);

    this.createVertexPoint(point);
    this.updatePositionBuffer(vertexIndex, point);
    this.indexCount++;
  }

  /**
   *  Adds existing vertex points starting from the current index of the current triangle.
   *
   * @param vertexIndices - The vertex indices to connect to the triangles.
   */
  private addExistingVertexToTriangleIndex(vertexIndices: number | number[]) {
    const vertexArray = Array.isArray(vertexIndices) ? vertexIndices : [vertexIndices];
    for (const vertexIndex of vertexArray) {
      if (vertexIndex < 0 || vertexIndex >= this.vertexPoints.length) {
        throw new Error('Vertex index is out of bounds.');
      }

      this.updateTriangleIndex(this.triangleCount, this.indexCount % 3, vertexIndex);
      this.indexCount++;
    }
  }

  /**
   *  Updates an index of a triangle with a new vertex index.
   *
   * @param triangleIndex - The triangle which index is to be updated with the new vertex index.
   * @param triangleIndex - The target triangle index which is to be updated with the new vertex index.
   * @param vertexIndex - The vertex index to be added to the triangle index.
   */
  private updateTriangleIndex(triangleId: number, triangleIndex: number, vertexIndex: number) {
    switch (triangleIndex) {
      case 0:
        this.geometry.index!.setX(triangleId, vertexIndex);
        break;
      case 1:
        this.geometry.index!.setY(triangleId, vertexIndex);
        break;
      case 2:
        this.geometry.index!.setZ(triangleId, vertexIndex);
        break;
    }
    this.geometry.index!.needsUpdate = true;
  }

  /**
   *  Updates the ghost point position and evaluates if it should be visible.
   *
   * @param edge - The edge on where to display the ghost point.
   */
  private updateGhostPoint(edge?: Edge) {
    this.ghostPoint.sphere.visible = !!edge;
    if (!edge) {
      return;
    }

    this.add(this.ghostPoint.sphere);

    const vPos0 = this.vertexPoints[edge.v0].sphere.position.clone();
    const vPos1 = this.vertexPoints[edge.v1].sphere.position.clone();
    const middleDist = vPos1.sub(vPos0).multiplyScalar(0.5);
    this.ghostPoint.sphere.position.copy(vPos0.add(middleDist));
    this.ghostPoint.edge = edge;
  }

  /**
   *  Returns true if picking the ghost point.
   *
   * @param screenPoint - The point on the screen to check picking against.
   */
  private isPickingGhostPoint(screenPoint: Vector2) {
    if (!this.ghostPoint.sphere.visible) {
      return false;
    }

    const vertexPos = this.ghostPoint.sphere.getWorldPosition(new Vector3());
    const distance = cameraManager()
      .getActiveCamera()!
      .getScreenPosition(vertexPos)
      .sub(screenPoint)
      .lengthSq();

    const pickingThreshold = 144;
    return distance < pickingThreshold;
  }

  /**
   *  Creates a new vertex at the ghost point position.
   */
  private createVertexPointAtGhostPoint() {
    this.removeUnfinishedTriangle();

    const position = this.ghostPoint.sphere.position;
    const newVertexPoint = this.createVertexPoint(position);
    this.updatePositionBuffer(this.vertexPoints.length - 1, position);

    // Extract the triangle of the edge.
    const triangleIndex = this.getTriangleIndexOfEdge(this.ghostPoint.edge!);
    const triangleIndices = [
      this.geometry.index!.getX(triangleIndex),
      this.geometry.index!.getY(triangleIndex),
      this.geometry.index!.getZ(triangleIndex),
    ];

    // Declare the triangle indices of the two triangles created from the split.
    const edgeStartIndex = triangleIndices.indexOf(this.ghostPoint.edge!.v0);
    const newTriangle1 = [
      triangleIndices[edgeStartIndex],
      this.vertexCount - 1,
      triangleIndices[(edgeStartIndex + 2) % 3],
    ];
    const newTriangle2 = [
      this.vertexCount - 1,
      triangleIndices[(edgeStartIndex + 1) % 3],
      triangleIndices[(edgeStartIndex + 2) % 3],
    ];

    // Remove the edges of the split triangle.
    this.removeTriangleEdges(triangleIndex);

    // Insert the two triangles into the geometry, an create their edges.
    this.geometry.index!.setXYZ(triangleIndex, newTriangle1[0], newTriangle1[1], newTriangle1[2]);
    this.addExistingVertexToTriangleIndex(newTriangle2);
    this.addTriangleEdges(triangleIndex);
    this.addTriangleEdges(this.triangleCount - 1);

    // This recreates the editor triangle.
    if (this.isEditing) {
      this.addNewVertexToTriangleIndex(position);
      this.addExistingVertexToTriangleIndex(this.vertexCount - 1);
      this.addExistingVertexToTriangleIndex(this.vertexCount - 1);
    }

    // Pick the new vertex, so that it is possible to move it around.
    this.pickedVertex = {
      point: newVertexPoint,
      oldPosition: position,
    };

    // Hide the ghost points while moving a vertex,
    this.remove(this.ghostPoint.sphere);

    // Update the draw range as there are an additional triangle after the split.
    this.geometry.setDrawRange(0, this.indexCount - (this.indexCount % 3));
  }

  /**
   *  Removes and clears the last vertex point.
   */
  private removeLatestVertexPoint() {
    PubSub.publish('onVolumeBoundaryPolygonChanged', this);
    const vertexPoint = this.vertexPoints.pop()!;
    this.remove(vertexPoint.sphere);

    vertexPoint.sphere.dispose();
  }

  /**
   *  Clears vertex and index data of the unfinished triangle.
   */
  private removeUnfinishedTriangle() {
    PubSub.publish('onVolumeBoundaryPolygonChanged', this);
    /**
     *  If there are at least 3 triangles available,
     *  iterate the vertices of the visual (latest) triangle, and identify all references.
     *  If it only used by the latest triangle, it can be removed.
     */
    if (this.vertexCount >= 3 && this.isEditing) {
      this.geometry.index!.setXYZ(this.triangleCount - 1, -1, -1, -1);
      const vertexReference = this.findAllVertexReferences(this.vertexCount - 1);
      if (vertexReference.length === 0) {
        this.removeLatestVertexPoint();
      }

      this.indexCount -= 3;
    }

    /**
     *  If there are less than 3 vertices or no triangles remain,
     *  clear and reset the polygon.
     */
    if (this.vertexCount < 3 || this.triangleCount === 0) {
      this.clearCustom();
    }

    this.geometry.setDrawRange(0, this.indexCount);
  }

  /**
   *  Returns all of the index elements that references a specific vertex index.
   *
   * @param vertexIndex - The vertex index to find references for.
   */
  private findAllVertexReferences(vertexIndex: number): number[] {
    const indexElements: number[] = [];
    _.times(this.indexCount, (index: number) => {
      if (this.getVertexIndexAtIndexElement(index) === vertexIndex) {
        indexElements.push(index);
      }
    });

    return indexElements;
  }

  /**
   *  Returns all of the triangle indices that references a specific vertex index.
   *
   * @param vertexIndex - The vertex index to find references for.
   */
  private findAllTriangleReferences(vertexIndex: number): number[] {
    const indexElements: Set<number> = new Set<number>();
    _.times(this.indexCount, (index: number) => {
      if (this.getVertexIndexAtIndexElement(index) === vertexIndex) {
        indexElements.add(Math.floor(index / 3));
      }
    });

    return Array.from(indexElements);
  }

  /**
   *  Returns true if the vertex index is referenced by the geometry indices.
   *
   * @param vertexIndex - The vertex index to find references for.
   */
  private isVertexReferenced(vertexIndex: number): boolean {
    for (let i = 0; i < this.indexCount; i++) {
      if (this.getVertexIndexAtIndexElement(i) === vertexIndex) {
        return true;
      }
    }

    return false;
  }

  /**
   *  Returns the vertex index at the index element.
   *
   * @param indexElement - The element in the index array from where to fetch the vertex index.
   */
  private getVertexIndexAtIndexElement(indexElement: number) {
    if (indexElement < 0 || indexElement >= this.indexCount) {
      throw new Error('Invalid index element. Index our of range.');
    }

    const triangle = Math.floor(indexElement / 3);
    return this.getVertexIndex(triangle, indexElement % 3);
  }

  /**
   *  Updates the position of the vertex point at a specific index.
   *
   * @param vertexIndex - The index of which vertex the position is to be updated.
   * @param pickedPosition - The position to set the vertex to.
   */
  private updateVertexPointPositionAtIndex(vertexIndex: number, position: Vector3) {
    if (vertexIndex < 0 || vertexIndex >= this.vertexCount) {
      throw new Error('Invalid index element. Index our of range.');
    }

    this.vertexPoints[vertexIndex].sphere.position.copy(position);
    this.updatePositionBuffer(vertexIndex, position);

    // Find all the triangle referencing the vertex point
    // and recreate all of the edges.
    const triangleReferences = this.findAllTriangleReferences(vertexIndex);
    for (const triangleIndex of triangleReferences) {
      // IF we are editing, skip the last triangle.
      if (this.isEditing && triangleIndex === this.triangleCount - 1) {
        continue;
      }
      this.removeTriangleEdges(triangleIndex);
      this.addTriangleEdges(triangleIndex);
    }
  }

  /**
   *  Returns the vertex index at specific triangle and internal index.
   *
   * @param triangle - The triangle from which to fetch the triangle vertex from.
   * @param triangleIndex - The internal triangle vertex index to return.
   */
  private getVertexIndex(triangle: number, triangleIndex: number) {
    switch (triangleIndex) {
      case 0:
        return this.geometry.index!.getX(triangle);
      case 1:
        return this.geometry.index!.getY(triangle);
      case 2:
        return this.geometry.index!.getZ(triangle);
      default:
        throw new Error('Invalid triangle index. Has to be number 0, 1 or 2.');
    }
  }

  /**
   *  Returns the triangle index of an edge.
   *
   *  @param edge - The edge of which triangle index to return.
   */
  private getTriangleIndexOfEdge(edge: Edge) {
    const triangleIndices = [-1, -1, -1];
    for (let i = 0; i < this.triangleCount; i++) {
      triangleIndices[0] = this.geometry.index!.getX(i);
      triangleIndices[1] = this.geometry.index!.getY(i);
      triangleIndices[2] = this.geometry.index!.getZ(i);
      if (triangleIndices.includes(edge.v0) && triangleIndices.includes(edge.v1)) {
        return i;
      }
    }

    return -1;
  }

  /**
   *  Returns the vertex indices for the polygon edge closest to the picking point in 2D.
   *
   *  @param picking - The point of origin from which the edge distances are calculated.
   */
  private getClosestPolygonEdge(screenPos: Vector2): [Edge, boolean] | undefined {
    const activeCamera = cameraManager().getActiveCamera();

    /**
     *  If the current available vertex points are below 2, there is no edge to find.
     */
    if (this.vertexPoints.length < 2) {
      return undefined;
    }

    let closestEdge: Edge | undefined;
    let shortestDistance = Number.MAX_SAFE_INTEGER;
    const triangleCount = this.triangleCount - (this.isEditing ? 1 : 0);
    for (let i = 0; i < triangleCount; i++) {
      const v0 = activeCamera?.getScreenPosition(
        this.vertexPoints[this.getVertexIndex(i, 0)].sphere.getWorldPosition(new Vector3()),
      )!;
      const v1 = activeCamera?.getScreenPosition(
        this.vertexPoints[this.getVertexIndex(i, 1)].sphere.getWorldPosition(new Vector3()),
      )!;
      const v2 = activeCamera?.getScreenPosition(
        this.vertexPoints[this.getVertexIndex(i, 2)].sphere.getWorldPosition(new Vector3()),
      )!;
      const edge = findClosestTriangleEdge(screenPos, v0, v1, v2);

      if (
        !edge ||
        !this.isAvailableEdge(
          this.getVertexIndex(i, edge.index[0]),
          this.getVertexIndex(i, edge.index[1]),
        )
      ) {
        continue;
      }

      const distance = distanceToLineSegment(screenPos, edge?.v0, edge?.v1);
      if (distance <= shortestDistance) {
        shortestDistance = distance;
        closestEdge = {
          v0: this.getVertexIndex(i, edge.index[0]),
          v1: this.getVertexIndex(i, edge.index[1]),
        };
      }
    }

    return closestEdge && [closestEdge, shortestDistance < 12];
  }

  /**
   *  Creates a line between two points.
   *
   * @param p1 - The position from where the line should start.
   * @param p2 - The position from where the line should end.
   */
  private setHelperLine(p1: Vector3, p2: Vector3) {
    if (this.helperLine) {
      this.remove(this.helperLine?.backgroundLine);
      this.remove(this.helperLine?.accentLine);

      this.helperLine?.backgroundLine.geometry.dispose();
      this.helperLine?.accentLine.geometry.dispose();
    }

    const lineGeometry = new LineGeometry();
    lineGeometry.setPositions([p1.x, p1.y, p1.z, p2.x, p2.y, p2.z]);

    this.helperLine = {
      backgroundLine: new Line2(lineGeometry, lineMaterial.background),
      accentLine: new Line2(lineGeometry, lineMaterial.accent),
    };

    this.add(this.helperLine.accentLine);
    this.add(this.helperLine.backgroundLine);

    this.helperLine.accentLine.renderOrder = renderOrders.lineAccent;
    this.helperLine.backgroundLine.renderOrder = renderOrders.lineBackground;
  }
}
